import { withApollo } from 'components/withApollo'
import { GetStaticPathsResult, NextPage } from 'next'
import createApolloClient from 'components/withApollo/createApolloClient'
import CATERING_PATHS_QUERY from 'components/pages/CateringPage/cateringPathsQuery'
import getStaticApolloProps from 'lib/getStaticApolloProps'
import { cateringPathsQuery } from 'components/pages/CateringPage/types/cateringPathsQuery'
import { useRouter } from 'next/router'
import React from 'react'
import HeroSection from 'components/pages/CateringPage/HeroSection'
import PageError from 'components/pages/PageError'
import Loading from 'components/Loading'
import { useCateringCategoryQuery } from 'components/pages/CateringPage/types/CateringCategoryQuery'
import PageMeta from 'components/PageMeta'
import PageLayout from 'components/pages/PageLayout'
import ContentSections from 'components/pages/CateringPage/ContentSections'
import FaqSection from 'components/pages/CateringPage/FaqSection'
import { ClientLogos } from 'components/ClientClouds'
import StatsSection from 'components/StatsSection'
import FeatureSection from 'components/pages/CateringMarketPage/FeatureSection'
import CtaSection from 'components/pages/CateringMarketPage/CtaSection'
import { useCateringCategoryMarketQuery } from 'components/pages/CateringPage/types/CateringCategoryMarketQuery'
import replaceCategoryVariables from 'components/pages/CateringPage/replaceCategoryVariables'
import NotFoundPage from 'components/pages/NotFoundPage/NotFoundPage'
import { useCateringCategoryStructuredDataQuery } from 'components/pages/CateringPage/types/CateringCategoryStructuredDataQuery'
import OtherCategories from 'components/pages/CateringPage/OtherCategories'
import CateringRatings from 'components/CateringRatings'
import FoodTypesSection from 'components/FoodTypesSection'

type Props = {}

type Params = {
  category?: string
  market?: string
}

function CateringPage() {
  const router = useRouter()
  const { category: categorySlug, market: marketSlug } = (router.query ??
    {}) as Params

  const mainPage = !categorySlug

  const { data, error, loading } = useCateringCategoryQuery({
    fetchPolicy: 'cache-and-network',
    variables: {
      seoName: mainPage ? 'main' : categorySlug!,
      marketSeoName: marketSlug ?? null
    }
  })
  const {
    data: marketData,
    error: marketError,
    loading: marketLoading
  } = useCateringCategoryMarketQuery({
    skip: !marketSlug,
    fetchPolicy: 'cache-and-network',
    variables: {
      seoName: marketSlug!
    }
  })
  const { data: structuredData } = useCateringCategoryStructuredDataQuery({
    fetchPolicy: 'cache-and-network',
    variables: {
      categorySeoName: mainPage ? 'main' : categorySlug!,
      marketSeoName: marketSlug ?? null
    }
  })
  if (error ?? marketError)
    return <PageError>{(error ?? marketError)!.message}</PageError>

  if (!data?.cateringCategory && loading)
    return <Loading className="w-full h-72" />

  const market = marketSlug ? marketData?.marketBySlug : null

  if (marketSlug && !marketData?.marketBySlug && marketLoading)
    return <Loading className="w-full h-72" />

  const { cateringCategory } = data!

  if (!cateringCategory || !cateringCategory.active)
    return <NotFoundPage search={`${marketSlug || ''}`} />

  return (
    <>
      <PageMeta
        canonicalPath={
          mainPage
            ? '/food-trucks/catering'
            : `/food-trucks/catering/${marketSlug ?? 'for'}/${categorySlug}`
        }
        coordinates={market ? [market.latitude, market.longitude] : null}
        description={replaceCategoryVariables({
          text: cateringCategory.metaDescription,
          market: market?.name
        })}
        image={cateringCategory.image?.small}
        ldJson={structuredData?.structuredData?.cateringCategoryPage}
        noIndex={!cateringCategory.active}
        title={`Best Food Trucks | ${replaceCategoryVariables({
          text: cateringCategory.title,
          market: market?.name
        })}`}
      />
      <PageLayout fullWidth>
        <div className="w-full">
          <HeroSection category={cateringCategory} market={market} />
          <ClientLogos
            clients={[
              'pfizer',
              'boeing',
              'apple',
              'amazon',
              'cushman',
              'aramark',
              'compass',
              'swedish'
            ]}
          />
          <StatsSection
            description="By managing food truck daily life from event booking, to online ordering and catering we have cultivated an intimate relationship with great vendors throughout the country and know exactly which truck(s) will suit your needs, from a small neighborhood party to massive corporate events."
            points={[
              {
                label: 'Cities',
                value: '1900+'
              },
              {
                label: 'Caterings',
                value: '2,000+'
              },
              {
                label: 'Attendees',
                value: '300,000+'
              },
              {
                label: 'Cuisines',
                value: '80+'
              }
            ]}
            title="Best Catering Expertise"
          />
          <ContentSections category={cateringCategory} market={market} />
          <CtaSection
            eventName={cateringCategory.mainPage ? null : cateringCategory.name}
            marketName={market?.name}
          />
          <FoodTypesSection foodTypePages={data!.foodTypePages} />
          <FeatureSection />
          <CateringRatings
            categorySlug={mainPage ? null : categorySlug}
            marketId={market?.id}
          />
          {cateringCategory.enableFaq && (
            <>
              <FaqSection category={cateringCategory} />
              <CtaSection
                eventName={
                  cateringCategory.mainPage ? null : cateringCategory.name
                }
                marketName={market?.name}
              />
            </>
          )}
          <OtherCategories category={cateringCategory} />
        </div>
      </PageLayout>
    </>
  )
}

export default withApollo({ ssr: false })(CateringPage as NextPage)

export async function getStaticPaths(): Promise<GetStaticPathsResult> {
  const client = createApolloClient({}, null)

  const { data } = await client.query<cateringPathsQuery>({
    query: CATERING_PATHS_QUERY
  })

  const paths = data.customerPaths.cateringCategoryPaths.map(
    (cateringPath) => ({
      params: { category: cateringPath }
    })
  )

  return {
    paths,
    fallback: 'blocking'
  }
}

export async function getStaticPathsMarket(): Promise<GetStaticPathsResult> {
  return {
    paths: [],
    fallback: 'blocking'
  }
}

export const getStaticProps = getStaticApolloProps<Props, Params>(
  CateringPage,
  {
    revalidate: 60 * 60 // 1 hour
  }
)
